import { Link } from "react-router-dom";
import { Flame, Sparkles, Lock, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SPORT_META, formatOdds, BET_LABEL } from "@/lib/picks";

interface Props {
  pick: any;
  sportCode: string;
  locked: boolean;
}

export default function HotPickHero({ pick, sportCode, locked }: Props) {
  const meta = SPORT_META[sportCode] ?? SPORT_META.NBA;
  const conf = Number(pick.confidence_score ?? 0);
  const edgePct = conf > 50 ? Math.max(0, (conf - 50) / 10) : 0;

  return (
    <div className="te-card relative overflow-hidden p-4 mb-5 border-primary/30 ring-1 ring-primary/20">
      <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-gradient-primary opacity-20 blur-3xl" />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className={`te-chip border ${meta.color}`}>{meta.icon} {meta.label}</span>
        <span className="te-chip border border-primary/40 bg-primary/15 text-primary text-[10px]">
          <Flame className="w-3 h-3 mr-1" /> Strongest Edge
        </span>
      </div>

      <div className="mt-3 relative">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {BET_LABEL[pick.bet_type] ?? "Featured"}
        </div>
        <h3 className="font-display font-bold text-lg leading-tight mt-1">
          {locked ? (pick.teams || "Featured Matchup") : (pick.title || pick.teams)}
        </h3>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <Mini label="Conf" value={`${conf || "—"}`} accent />
        <Mini label="Edge" value={edgePct > 0 ? `+${edgePct.toFixed(1)}%` : "—"} accent />
        <Mini label="Odds" value={locked ? "—" : formatOdds(pick.odds)} />
      </div>

      <ConfidenceBar value={conf} />

      {!locked && pick.ai_summary && (
        <p className="mt-3 text-xs text-muted-foreground line-clamp-2 leading-relaxed">
          {pick.ai_summary}
        </p>
      )}
      {locked && (
        <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
          <Lock className="w-3 h-3 text-accent shrink-0" /> Premium reasoning locked
        </p>
      )}

      <Link to={`/picks/${pick.id}`} className="block mt-3">
        <Button className="w-full bg-gradient-primary text-primary-foreground hover:opacity-95 active:scale-[0.99] transition">
          {locked ? <><Sparkles className="w-4 h-4 mr-1.5" /> Unlock breakdown</> : <>View breakdown <ChevronRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </Link>
    </div>
  );
}

function Mini({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-lg bg-secondary/50 border border-border/50 px-2.5 py-2">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground leading-none">{label}</div>
      <div className={`font-display font-bold text-base mt-1 tabular-nums leading-none ${accent ? "te-grad-text" : ""}`}>{value}</div>
    </div>
  );
}

function ConfidenceBar({ value }: { value: number }) {
  if (!value) return null;
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5">
        <span>Model confidence</span>
        <span className="tabular-nums font-semibold text-foreground">{value}/100</span>
      </div>
      <div className="h-2.5 rounded-full bg-secondary/80 overflow-hidden ring-1 ring-border/50">
        <div
          className="h-full rounded-full bg-gradient-to-r from-primary via-primary to-accent shadow-[0_0_12px_hsl(var(--primary)/0.55)] animate-[confidence-fill_700ms_ease-out_both]"
          style={{ width: `${Math.min(100, value)}%` }}
        />
      </div>
    </div>
  );
}
